import React, { useState, useEffect } from 'react';
import { Select, Autocomplete,TablePagination,Menu,Tooltip,Popover } from '@mui/material';
import { DatePicker, DateTimePicker, TimePicker } from '@mui/x-date-pickers';
import PropTypes from 'prop-types';
import { useModalManager } from './ModalManagerContext'; 
import { overlayEventProps } from './managedOverlayEvents';

// Thêm cấu hình ánh xạ các Component Date/Time vào map
const MUI_COMPONENTS = {
  select: Select,
  datepicker: DatePicker,
  datetimepicker: DateTimePicker,
  timepicker: TimePicker,
  autocomplete: Autocomplete,
  tablepagination: TablePagination,
  menu:Menu,
  tooltip:Tooltip,
  popover:Popover
};

/**
 * Component quản lý Z-Index động cho Dropdown, Autocomplete và các bộ chọn Date/Time Picker
 * để đảm bảo menu/popper của chúng luôn nổi lên trên đúng lớp Modal/Dialog hiện tại.
 * * @param {Object} props
 * @param {'select' | 'autocomplete' | 'datepicker' | 'datetimepicker' | 'timepicker' | 'tablepagination' | 'menu' | 'tooltip' | 'popover'} [props.type='select']
 */
export default function ManagedDropdown({ 
  type = 'select', 
  onOpen,
  onClose,
  ...props 
}) {
  const modalManager = useModalManager();
  const requestZIndex = modalManager?.requestZIndex;
  const releaseZIndex = modalManager?.releaseZIndex;

  const [dropdownId] = useState(() => `managed_${type}_` + Date.now() + Math.random().toString(36).slice(2, 7));
  const [localZIndex, setLocalZIndex] = useState(undefined);
  
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = props.open !== undefined ? props.open : internalOpen;

  const handleOpen = (event) => {
    setInternalOpen(true);
    if (onOpen) onOpen(event);
  };

  // MUI DatePicker truyền các tham số đóng khác với Select thường, 
  // dùng toán tử rest (...args) để giữ nguyên cơ chế của MUI gốc
  const handleClose = (...args) => {
    setInternalOpen(false);
    if (onClose) onClose(...args);
  };

  useEffect(() => {
    if (isOpen && requestZIndex) {
      const grantedZIndex = requestZIndex(dropdownId);
      setLocalZIndex(grantedZIndex);
    }
    
    return () => {
      if (releaseZIndex) {
        releaseZIndex(dropdownId);
      }
    };
  }, [isOpen, dropdownId, requestZIndex, releaseZIndex]);

  const ComponentToRender = MUI_COMPONENTS[type];

  if (!ComponentToRender) {
    console.error(`ManagedDropdown: Không hỗ trợ type '${type}'.`);
    return null;
  }

  const zIndexStyle = localZIndex !== undefined ? { zIndex: localZIndex } : {};
  let specificProps = {};

  // --- PHÂN LUỒNG INJECT Z-INDEX VÀO PROPS CỦA TỪNG LOẠI COMPONENT ---
  
  if (type === 'select') {
    specificProps = {
      MenuProps: {
        ...props.MenuProps,
        sx: { ...props.MenuProps?.sx, ...zIndexStyle }
      }
    };
  }
  if (type === 'menu' || type === 'popover') {
    specificProps = {
      slotProps: {
        root: { sx: { zIndex: localZIndex } }
      }
    };
  } 
  else if (type === 'tooltip') {
    specificProps = {
      slotProps: {
        popper: { sx: { zIndex: localZIndex } }
      }
    };
  }
  else if (type === 'tablepagination') {
    specificProps = {
      slotProps: {
        ...props.slotProps,
        select: {
          ...props.slotProps?.select,
          // Bắt sự kiện Open/Close từ Select nằm trong TablePagination
          onOpen: handleOpen,
          onClose: handleClose,
          MenuProps: {
            ...props.slotProps?.select?.MenuProps,
            sx: { ...props.slotProps?.select?.MenuProps?.sx, ...zIndexStyle }
          }
        }
      }
    };
  }
  else if (type === 'autocomplete') {
    specificProps = {
      slotProps: {
        ...props.slotProps,
        popper: {
          ...props.slotProps?.popper,
          sx: { ...props.slotProps?.popper?.sx, ...zIndexStyle }
        }
      },
      componentsProps: {
        ...props.componentsProps,
        popper: {
          ...props.componentsProps?.popper,
          sx: { ...props.componentsProps?.popper?.sx, ...zIndexStyle }
        }
      }
    };
  } 
  // Gom nhóm các loại picker vì chúng có cấu trúc cây slotProps.popper giống hệt nhau
  else if (['datepicker', 'datetimepicker', 'timepicker'].includes(type)) {
    specificProps = {
      //desktopModeMediaQuery: '@media (min-width: 0px)',
      // Ép sự kiện Open/Close thông qua API chuẩn của X-Date-Pickers
      onOpen: handleOpen,
      onClose: handleClose,
      slotProps: {
        ...props.slotProps,
        popper: {
          ...props.slotProps?.popper,
          sx: { 
            ...props.slotProps?.popper?.sx, 
            ...zIndexStyle // Tiêm z-index cấp phát từ Context vào khung lịch rơi xuống
          }
        },
        dialog: {
          ...props.slotProps?.dialog,
          sx: { ...props.slotProps?.dialog?.sx, ...zIndexStyle }
        }
      }
    };
  }

  // NỐI LẠI sự kiện đóng/mở đã bị bóc khỏi props ở đầu component.
  // Trước đây chỉ nối cho select/autocomplete -> menu/popover/tooltip MẤT `onClose`: mở menu ra thì bấm
  // ra ngoài không tắt được, phải chọn một mục mới thoát. Danh sách theo từng loại nằm ở
  // managedOverlayEvents.js (có test chốt), vì thừa một prop là React cảnh báo prop lạ trên DOM.
  const commonEvents = {};
  for (const eventName of overlayEventProps(type)) {
    commonEvents[eventName] = eventName === 'onOpen' ? handleOpen : handleClose;
  }

  return (
    <ComponentToRender
      {...props}
      {...commonEvents}
      {...specificProps}
    />
  );
}

ManagedDropdown.propTypes = {
  type: PropTypes.oneOf(['select', 'autocomplete', 'datepicker', 'datetimepicker', 'timepicker','tablepagination', 'menu' , 'tooltip' , 'popover']),
  open: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
};